/**
 * Portfolio Thesis Change Detection Utilities
 *
 * Functions for detecting and formatting changes between portfolio thesis versions.
 * Used by the PortfolioThesisChangelog component to display version history.
 */

import type { PortfolioThesis } from '../services/api/portfolioThesisApi';
export { formatVersionDate } from './dateFormatting';
export { truncateText as truncateCommentText } from './textTruncation';

export interface PortfolioFormattedChange {
  text: string;
  type: 'risk' | 'percentage' | 'currency' | 'text';
}

/**
 * Risk level mappings for translation
 */
const RISK_LABELS: Record<string, string> = {
  Conservative: 'Conservative',
  Moderate: 'Moderate',
  Balanced: 'Balanced',
  Growth: 'Growth',
};

/**
 * Compare two portfolio thesis versions and return change summary.
 *
 * @param olderVersion - The older version (what changed FROM)
 * @param newerVersion - The newer version (what changed TO)
 * @param t - i18n translation function
 * @param currency - Currency symbol for cash flow display
 * @returns Array of formatted change strings, or null if no changes
 */
export function detectPortfolioThesisChanges(
  olderVersion: PortfolioThesis,
  newerVersion: PortfolioThesis,
  t?: any,
  currency: string = ''
): PortfolioFormattedChange[] | null {
  const changes: PortfolioFormattedChange[] = [];

  // Check risk level
  if (olderVersion.target_risk_level !== newerVersion.target_risk_level) {
    const oldVal = olderVersion.target_risk_level || '—';
    const newVal = newerVersion.target_risk_level || '—';
    const text = t
      ? t('portfolio.riskLevelChanged', {
          defaultValue: 'Risk Level: {{old}} → {{new}}',
          old: formatRiskLabel(oldVal, t),
          new: formatRiskLabel(newVal, t),
        })
      : `Risk Level: ${formatRiskLabel(oldVal)} → ${formatRiskLabel(newVal)}`;
    changes.push({ text, type: 'risk' });
  }

  // Check return level
  if (olderVersion.target_return_level !== newerVersion.target_return_level) {
    const oldVal = olderVersion.target_return_level ?? '—';
    const newVal = newerVersion.target_return_level ?? '—';
    const oldStr = oldVal === '—' ? '—' : `${Number(oldVal).toFixed(1)}%`;
    const newStr = newVal === '—' ? '—' : `${Number(newVal).toFixed(1)}%`;
    changes.push({
      text: t
        ? t('portfolio.returnChanged', {
            defaultValue: 'Return: {{old}} → {{new}}',
            old: oldStr,
            new: newStr,
          })
        : `Return: ${oldStr} → ${newStr}`,
      type: 'percentage',
    });
  }

  // Check cash flow
  if (olderVersion.target_cash_flow !== newerVersion.target_cash_flow) {
    const oldVal = olderVersion.target_cash_flow ?? '—';
    const newVal = newerVersion.target_cash_flow ?? '—';
    const currencyStr = currency || '';
    const oldStr = oldVal === '—' ? '—' : `${currencyStr}${Number(oldVal).toFixed(2)}`;
    const newStr = newVal === '—' ? '—' : `${currencyStr}${Number(newVal).toFixed(2)}`;
    changes.push({
      text: t
        ? t('portfolio.cashFlowChanged', {
            defaultValue: 'Cash Flow: {{old}} → {{new}}',
            old: oldStr,
            new: newStr,
          })
        : `Cash Flow: ${oldStr} → ${newStr}`,
      type: 'currency',
    });
  }

  // Check comment
  const oldComment = olderVersion.comment || '';
  const newComment = newerVersion.comment || '';
  const oldHasComment = oldComment.trim().length > 0;
  const newHasComment = newComment.trim().length > 0;

  if (oldHasComment !== newHasComment) {
    // Comment was added or removed
    const text = t
      ? newHasComment
        ? t('portfolio.strategyAdded', { defaultValue: 'Strategy added' })
        : t('portfolio.strategyRemoved', { defaultValue: 'Strategy removed' })
      : newHasComment
        ? 'Strategy added'
        : 'Strategy removed';
    changes.push({ text, type: 'text' });
  } else if (oldHasComment && newHasComment && oldComment.trim() !== newComment.trim()) {
    // Comment was modified
    const text = t
      ? t('portfolio.strategyUpdated', { defaultValue: 'Strategy updated' })
      : 'Strategy updated';
    changes.push({ text, type: 'text' });
  }

  return changes.length > 0 ? changes : null;
}

/**
 * Format risk level label with translation.
 */
function formatRiskLabel(risk: string | null | undefined, t?: any): string {
  if (!risk || risk === '—') return '—';

  if (t) {
    const key = `portfolio.risk${risk}`;
    return t(key, { defaultValue: RISK_LABELS[risk] || risk });
  }

  return RISK_LABELS[risk] || risk;
}
